import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { defineMessages, T, t, FormattedNumber } from '@sm/intl';
import { createUseStyles, ThemeProvider, useTheme } from 'react-jss';
import { Tooltip } from '@wds/tooltip';
import Highcharts from 'highcharts';
import wordCloudModule from 'highcharts/modules/wordcloud';
import { c as classNames, H as HighchartsReact } from './vendor-54f793eb.js';
import { renderToString, renderToStaticMarkup } from 'react-dom/server';
import { safeRest } from '@wds/utils';
import { T as Theme } from './theme-92ff4ea7.js';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import merge from 'lodash.merge';
import '@wds/styles';

var _a$2;
/**
 * SSR Safe version of useLayoutEffect
 * @see https://code.corp.surveymonkey.com/webplatform/smweb/blob/master/apps/contentweb/src/app/pages/MRX/helpers/useIsomorphicLayoutEffect.js
 *
 * Note: `NOSONAR` is used to bypass a `minor:code-smell` error that states `createElement` is deprecated.
 * The eslint rule above disables the spaced comments to use NOSONAR as stated in the docs.
 */


const useSSRSafeLayoutEffect = typeof window !== 'undefined' && typeof ((_a$2 = window === null || window === void 0 ? void 0 : window.document) === null || _a$2 === void 0 ? void 0 : _a$2.createElement) !== 'undefined' //NOSONAR
? useLayoutEffect : useEffect;

function useDimensions() {
  const target = useRef(null);
  const [dimensions, setDimensions] = useState({});
  useSSRSafeLayoutEffect(() => {
    if (target.current) {
      const rect = {
        width: target.current.offsetWidth,
        height: target.current.offsetHeight
      };
      setDimensions(rect);
    }
  }, []);
  return [target, dimensions];
}
/* eslint-disable react/no-array-index-key */


const SpacerSquares = ({
  range,
  width,
  height,
  offset,
  spacing
}) => React.createElement(React.Fragment, null, range.map((r, i) => React.createElement(React.Fragment, {
  key: `spacer-rect-${i}`
}, React.createElement("rect", {
  fill: "white",
  height: height,
  width: spacing * 2,
  x: i * offset + width,
  y: 0,
  style: {
    zIndex: 999
  }
}))));

const RatingBarProgress = ({
  rating,
  fill,
  width,
  height,
  maxRating,
  spacing = 0
}) => {
  const animationRef = useRef();
  const prevRating = useRef(0);
  useSSRSafeLayoutEffect(() => {
    prevRating.current = rating;
    const animation = animationRef === null || animationRef === void 0 ? void 0 : animationRef.current;

    if (animation && animation.beginElement) {
      animation.beginElement();
    }
  }, [rating]); // Adjust for spacer-gaps; progress does not take this in to account normally.

  const spacerPercent = spacing * 2 / 100;
  return React.createElement("rect", {
    fill: fill,
    height: height - 0.2,
    width: width,
    x: 0,
    y: 0.1
  }, React.createElement("animate", {
    ref: r => {
      animationRef.current = r;
    },
    attributeName: "width",
    from: `${(prevRating.current / maxRating - spacerPercent) * 100}%`,
    to: `${(rating / maxRating - spacerPercent) * 100}%`,
    dur: "1s",
    fill: "freeze"
  }));
};
/* eslint-disable react/no-array-index-key */


const RatingIcons = ({
  range,
  width,
  offset,
  iconComponent: Icon
}) => React.createElement(React.Fragment, null, range.map((r, i) => React.createElement(React.Fragment, {
  key: `icon-rect-${i}`
}, React.createElement("g", null, React.createElement(Icon, {
  x: i * offset + width / 4,
  width: width / 2,
  title: "",
  color: "light"
})))));
/* eslint-disable react/no-array-index-key */


const EmptySquares = ({
  range,
  fill,
  width,
  height,
  offset
}) => React.createElement(React.Fragment, null, range.map((r, i) => React.createElement("rect", {
  key: `empty-rect-${i}`,
  fill: fill,
  height: height,
  width: width,
  x: i * offset,
  y: 0
}))); // svg size parameter to ensure scaling


const baseSize$2 = 10; // spacing between block elements

const spacing$1 = 0.5; // offset calculation for spacing between elements

const xOffset = Math.ceil((baseSize$2 * 2 + spacing$1) / 2);

const RatingBar = ({
  maxRating = 5,
  rating,
  fillColor,
  emptyColor = '#D0D2D3',
  iconComponent
}) => {
  if (!Number.isFinite(rating)) {
    return null;
  } // dynamic box size calculation for svg scaling


  const boxSizeX = Math.ceil((baseSize$2 + spacing$1) * maxRating + spacing$1 * maxRating);
  const range = Array.from(new Array(maxRating));
  return React.createElement("svg", {
    viewBox: `0 0 ${boxSizeX} ${baseSize$2}`,
    preserveAspectRatio: "xMidYMid",
    width: "100%",
    height: "100%"
  }, React.createElement(EmptySquares, {
    range: range,
    width: baseSize$2,
    height: baseSize$2,
    fill: emptyColor,
    offset: xOffset
  }), React.createElement(RatingBarProgress, {
    fill: fillColor,
    height: baseSize$2,
    width: baseSize$2,
    rating: rating,
    maxRating: maxRating,
    spacing: spacing$1
  }), React.createElement(SpacerSquares, {
    range: range,
    width: baseSize$2,
    height: baseSize$2,
    offset: xOffset,
    spacing: spacing$1
  }), React.createElement(RatingIcons, {
    range: range,
    width: baseSize$2,
    offset: xOffset,
    iconComponent: iconComponent
  }));
};

const useStyles$8 = createUseStyles(theme => ({
  container: ({
    width = 0,
    height = 0
  }) => ({
    width: width || '100%',
    height: height || '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  scaleContainer: {
    display: 'inline-block',
    transform: ({
      scaleAmount
    }) => `scale(${scaleAmount})`
  },
  content: {
    padding: theme.spacing[3]
  }
}));
const useStyles$7 = createUseStyles(theme => ({
  ratingWrapper: {
    maxWidth: '200px',
    display: 'flex',
    paddingTop: theme.spacing[3]
  }
}));

const RatingWrapper = ({
  children
}) => {
  const {
    ratingWrapper
  } = useStyles$7();
  return React.createElement("div", {
    className: ratingWrapper
  }, children);
};

const COPY$4 = defineMessages({
  AVERAGE_RATING: {
    id: 'tablist.AVERAGE_RATING',
    defaultMessage: `average rating`,
    description: '[Type: label][Vis: high] - average rating title'
  }
});

function useScale(outer = {}, inner = {}, {
  min = 0,
  max = 10
}) {
  const [scale, setScale] = React.useState(1);
  React.useMemo(() => {
    if (outer.width && outer.height && inner.width && inner.height) {
      const size = Math.min(outer.height / inner.height, outer.width / inner.width);
      const result = Math.max(min, Math.min(size, max));
      setScale(result);
    }
  }, [inner.height, inner.width, outer.height, outer.width, min, max]);
  return scale;
}
/** Rating Chart */


const RatingChart = ({
  rating,
  fillColor = '#3FC688',
  maxRating = 5,
  iconComponent: Icon,
  width,
  height,
  scale = {
    max: 1.3
  }
}) => {
  const [containerRef, outerDimensions] = useDimensions();
  const [contentRef, innerDimensions] = useDimensions();
  const scaleAmount = useScale(outerDimensions, innerDimensions, scale);
  const {
    container,
    scaleContainer,
    content
  } = useStyles$8({
    width,
    height,
    scaleAmount
  });

  if (!Number.isFinite(rating)) {
    return null;
  }

  const normalizedRating = Math.min(Math.max(rating, 0), maxRating);
  return React.createElement("div", {
    ref: containerRef,
    className: container
  }, React.createElement("div", {
    className: scaleContainer
  }, React.createElement("div", {
    ref: contentRef,
    className: content
  }, React.createElement(Typography, {
    variant: "hero1",
    align: "center",
    key: "hero-title"
  }, normalizedRating.toFixed(1), React.createElement("span", {
    style: {
      color: fillColor
    }
  }, React.createElement(Icon, {
    title: "",
    size: "lg"
  }))), React.createElement(Typography, {
    variant: "sectionTitle",
    align: "center",
    key: "hero-subtitle"
  }, React.createElement(T, {
    desc: COPY$4.AVERAGE_RATING
  })), React.createElement(Box, {
    display: "flex",
    flexJustify: "center",
    flexAlign: "center",
    key: "rating-bar"
  }, React.createElement(RatingWrapper, {
    key: "rating-wrapper"
  }, React.createElement(RatingBar, {
    key: "rating-bar",
    maxRating: maxRating,
    rating: normalizedRating,
    fillColor: fillColor,
    iconComponent: Icon
  }))))));
};
/* eslint-disable import/prefer-default-export */


const RatingLegendProgress = ({
  emptyColor,
  fillColor,
  width,
  height,
  rating,
  percentage
}) => {
  const animationRef = useRef();
  const prevPercentage = useRef(0);
  useSSRSafeLayoutEffect(() => {
    prevPercentage.current = percentage;
    const animation = animationRef === null || animationRef === void 0 ? void 0 : animationRef.current;

    if (animation && animation.beginElement) {
      animation.beginElement();
    }
  }, [percentage]);
  return React.createElement("g", null, React.createElement("rect", {
    key: `empty-rect-${rating}`,
    fill: emptyColor,
    height: height,
    width: width,
    x: 0,
    y: 0
  }), React.createElement("rect", {
    fill: fillColor,
    height: height,
    width: 0,
    x: 0,
    y: 0
  }, React.createElement("animate", {
    key: `legend-animation-${rating}`,
    ref: r => {
      animationRef.current = r;
    },
    attributeName: "width",
    from: `${prevPercentage.current * 100}%`,
    to: `${percentage * 100}%`,
    dur: "1s",
    fill: "freeze"
  })));
};

const COPY$3 = defineMessages({
  ANSWERED: {
    id: 'ratingchart.ANSWERED',
    defaultMessage: `Answered: {number}`,
    description: '[Type: label][Vis: high] - amount of responses answered'
  },
  SKIPPED: {
    id: 'ratingchart.SKIPPED',
    defaultMessage: `Skipped: {skipped}`,
    description: '[Type: label][Vis: high] - amount of responses skippeed'
  },
  TOOLTIP: {
    id: 'ratingchart.TOOLTIP',
    defaultMessage: `{label} ({percent, number, percent})`,
    description: '[Type: label][Vis: high] - percentage tooltip'
  },
  RATING: {
    id: 'progress.RATING',
    defaultMessage: `{rating} star`,
    description: '[Type: label][Vis: high] - rating amount'
  }
}); // Fixes 'Rating Star Labels' (i.e. 1 Star, 5 Star, etc) wrapping on two lines when viewport is
// smaller than 430px.
// 2.666666666666667em = 40px
// Resolves: #ANEXP-1338, #RAWR-559

const useStyles$6 = createUseStyles({
  ratingLabelColumnStyles: {
    minWidth: '2.666666666666667em'
  },
  ratingProgressLine: {
    maxWidth: '100%'
  }
});
const baseSize$1 = 20;
const spacing = 0.5;
const height = 2;

const RatingChartLegend = ({
  ratings = [],
  total = 0,
  skipped = 0,
  fillColor = '#3FC688',
  emptyColor = '#D0D2D3',
  maxRating = 5,
  displaySkipped = 'auto'
}) => {
  const {
    ratingLabelColumnStyles,
    ratingProgressLine
  } = useStyles$6();
  const ratingsDesc = [...ratings].reverse();
  const displaySkipValue = typeof displaySkipped === 'boolean' ? displaySkipped : skipped > 0;
  const boxSize = Math.ceil((baseSize$1 + spacing) * maxRating + spacing * maxRating);
  return React.createElement(React.Fragment, null, React.createElement(Box, {
    key: "rating-response-text",
    display: "flex",
    flexDirection: "row",
    flexJustify: "center",
    p: 3
  }, React.createElement(Typography, {
    variant: "bodySm",
    align: "center",
    px: 3
  }, React.createElement(T, {
    desc: COPY$3.ANSWERED,
    values: {
      number: total
    }
  })), displaySkipValue && React.createElement(Typography, {
    variant: "bodySm",
    align: "center",
    px: 3
  }, React.createElement(T, {
    desc: COPY$3.SKIPPED,
    values: {
      skipped
    }
  }))), ratingsDesc.map(({
    rating,
    percentage,
    total: ratingTotal
  }) => React.createElement(Box, {
    key: `rating-${rating}`,
    display: "flex",
    flexAlign: "center",
    p: 1,
    flexJustify: "center"
  }, React.createElement(Box, {
    key: `rating-text-wrapper-${rating}`,
    display: "flex",
    mr: 2
  }, React.createElement("div", {
    className: ratingLabelColumnStyles
  }, React.createElement(Typography, {
    key: `rating-text-${rating}`,
    variant: "bodySm"
  }, React.createElement(T, {
    desc: COPY$3.RATING,
    values: {
      rating
    }
  })))), React.createElement(Box, {
    key: `rating-svg-wrapper-${rating}`,
    display: "flex",
    flexJustify: "center"
  }, React.createElement(Tooltip, {
    key: `rating-tooltip-${rating}`,
    text: t(COPY$3.TOOLTIP, {
      label: ratingTotal,
      percent: percentage
    })
  }, React.createElement("svg", {
    className: ratingProgressLine,
    key: `rating-svg-${rating}`,
    viewBox: `0 0 ${boxSize} ${height}`,
    preserveAspectRatio: "xMidYMid",
    width: "100%",
    height: "100%"
  }, React.createElement(RatingLegendProgress, {
    key: `rating-progress-${rating}`,
    emptyColor: emptyColor,
    fillColor: fillColor,
    rating: String(rating),
    width: boxSize,
    height: height,
    maxRating: maxRating,
    percentage: percentage
  })))))));
};

const tooltipColor = '#333E48';
const minTooltipHeight = 30;
const useStyles$5 = createUseStyles({
  wrapper: {
    minWidth: '50px',
    minHeight: `${minTooltipHeight}px`,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '8px'
  },
  arrow: {
    '&:after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      top: '100%',
      width: '0',
      height: '0',
      borderLeft: '20px solid transparent',
      borderRight: '20px solid transparent',
      borderTop: `20px solid ${tooltipColor}`,
      clear: 'both'
    }
  }
});
const COPY$2 = defineMessages({
  TOOLTIP_TITLE: {
    id: 'WordCloudTooltip.TOOLTIP_TITLE',
    defaultMessage: '#{number} most important word',
    description: '[Type: header][Vis: med] - title of the chart'
  },
  OCCURRENCES: {
    id: 'WordCloudTooltip.TOOLTIP_OCCURRENCES',
    defaultMessage: 'Occurrences: {weight} ',
    description: '[Type: header][Vis: med] - title of the chart'
  }
}); // TODO: Port tooltip here to @wds/tooltip - not ported yet

const WordCloudTooltip = ({
  point
}) => {
  const isTop = point.rect.top < -40;
  const styles = useStyles$5();
  return React.createElement("div", {
    "data-testid": `word-${point.name}`,
    className: classNames([styles.wrapper, !isTop ? styles.arrow : ''])
  }, React.createElement(T, {
    desc: COPY$2.TOOLTIP_TITLE,
    values: {
      number: point.top
    }
  }), React.createElement("span", null, React.createElement(T, {
    desc: COPY$2.OCCURRENCES,
    values: {
      weight: point.weight,
      percentage: point.weight
    }
  }), React.createElement(React.Fragment, null, "("), React.createElement(FormattedNumber, {
    formatStyle: "percent",
    maximumFractionDigits: 2,
    value: point.percentage
  }), React.createElement(React.Fragment, null, ")")));
};

const tooltipConfig = {
  backgroundColor: tooltipColor,
  borderColor: tooltipColor,
  style: {
    color: 'white'
  },
  useHTML: true,

  formatter() {
    // Typescript bindings of highcharts binding of "this.point" missing

    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    const {
      point
    } = this;
    return renderToString(React.createElement(WordCloudTooltip, {
      point: point
    }));
  }

};

const getWeightBy = func => data => data.reduce((max, b) => func(max, b.weight), data[0].weight);

const colorizeModel = (data, colorPalette) => {
  const minOccurrence = getWeightBy(Math.min)(data);
  const maxOccurrence = getWeightBy(Math.max)(data);
  const spread = maxOccurrence - minOccurrence;
  const margin = Math.ceil(spread / colorPalette.length);
  return data.map(w => {
    const colorIndex = (w.weight - minOccurrence) / margin;
    return { ...w,
      color: colorPalette[colorIndex]
    };
  });
};

const transformToHighChartsWordCloudModel = (words, total, fixedColor) => words.map((w, i) => ({
  name: w.word,
  weight: w.occurrences,
  percentage: w.percentage ? w.percentage : w.occurrences / total,
  // determine top word order
  top: words.length - i,
  color: fixedColor
}));

const LegendBlocks = ({
  range,
  width,
  scale,
  onFill
}) => {
  const rangeMap = Array.from(new Array(range));
  return React.createElement(React.Fragment, null, rangeMap.map((a, i) => {
    const size = width + i * 2;
    const posX = i * Math.ceil((size + 10) / 2);
    const posY = (scale - size) / 2;
    return (
      /* eslint-disable-next-line react/no-array-index-key */
      React.createElement("g", {
        key: `part-${i}`
      }, React.createElement("rect", {
        width: size,
        height: size,
        x: posX,
        y: posY,
        rx: 3,
        style: {
          fill: onFill(i)
        }
      }))
    );
  }));
};

const useStyles$4 = createUseStyles(theme => ({
  line: {
    stroke: theme.borderColor.hover,
    strokeWidth: 0.7
  },
  legendText: ({
    range
  }) => ({
    fontSize: `${range}px`,
    fill: theme.borderColor.hover
  })
}));

const LegendLabel = ({
  range,
  width,
  isStart,
  scale,
  text
}) => {
  const styles = useStyles$4({
    range
  });
  const index = isStart ? 0 : range - 1;
  const size = width + index * 2;
  const posX = index * Math.ceil((size + 10) / 2);
  const posY = (scale - size) / 2;
  const textPosY = posY + size + (range + 6);
  return React.createElement(React.Fragment, null, React.createElement("g", null, React.createElement("g", null, React.createElement("line", {
    className: styles.line,
    x1: posX + size / 2,
    y1: posY + size + 1,
    x2: posX + size / 2,
    y2: posY + size + 6
  })), React.createElement("g", null, React.createElement("text", {
    x: posX + size / 2,
    y: textPosY,
    textAnchor: "middle",
    className: styles.legendText
  }, text))));
};

const baseSize = 9;
const maxWidthFactor = 34;
const baseMaxWidth = 270;
const defaultLength = 8;
const useStyles$3 = createUseStyles(theme => ({
  legendContainer: ({
    maxWidth
  }) => ({
    border: `1px solid ${theme.semanticColor.muted}`,
    paddingBottom: theme.spacing['5'],
    paddingLeft: theme.spacing['4'],
    paddingRight: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
    maxWidth,
    width: '100%',
    height: '100%'
  }),
  legendWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  legendTitle: {
    textAlign: 'center',
    color: theme.borderColor.hover,
    fontSize: '11px',
    paddingBottom: theme.spacing['2']
  },
  legendLess: {
    flex: 0.2,
    alignSelf: 'center',
    textAlign: 'center',
    paddingLeft: theme.spacing['2'],
    paddingRight: theme.spacing['2'],
    color: theme.borderColor.hover,
    fontSize: '11px'
  },
  legendMore: {
    flex: 0.2,
    paddingLeft: theme.spacing['2'],
    paddingRight: theme.spacing['2'],
    color: theme.borderColor.hover,
    fontSize: '11px'
  },
  legend: {
    flex: 1
  }
}));
const COPY$1 = defineMessages({
  TITLE: {
    id: 'WordCloudLegend.TITLE',
    defaultMessage: 'Number of mentions',
    description: '[Type: header][Vis: med] - title of the legend'
  },
  FEWER: {
    id: 'WordCloudLegend.FEWER',
    defaultMessage: 'fewer',
    description: '[Type: header][Vis: med] - fewer'
  },
  MORE: {
    id: 'WordCloudLegend.MORE',
    defaultMessage: 'more',
    description: '[Type: header][Vis: med] - more'
  }
});

const WordCloudLegend = ({
  fixedColor = '#000',
  colorPalette = [],
  min,
  max
}) => {
  const range = colorPalette.length ? colorPalette.length : defaultLength;
  const scaleY = range * 4;
  const maxWidth = Math.max(baseMaxWidth, range * maxWidthFactor);
  const styles = useStyles$3({
    maxWidth
  });

  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    return null;
  }

  return React.createElement("div", {
    className: styles.legendContainer
  }, React.createElement("div", {
    className: styles.legendTitle
  }, React.createElement(T, {
    desc: COPY$1.TITLE
  })), React.createElement("div", {
    className: styles.legendWrapper
  }, React.createElement("div", {
    className: styles.legendLess
  }, React.createElement(T, {
    desc: COPY$1.FEWER
  })), React.createElement("div", {
    className: styles.legend
  }, React.createElement("svg", {
    viewBox: `0 0 ${range * (range + 10)} ${scaleY}`,
    overflow: "visible",
    preserveAspectRatio: "xMidYMid"
  }, React.createElement(LegendBlocks, {
    range: range,
    width: baseSize,
    scale: scaleY,
    onFill: pos => !(colorPalette === null || colorPalette === void 0 ? void 0 : colorPalette.length) ? fixedColor : colorPalette[pos]
  }), React.createElement(LegendLabel, {
    range: range,
    width: baseSize,
    isStart: true,
    scale: scaleY,
    text: min
  }), React.createElement(LegendLabel, {
    range: range,
    width: baseSize,
    scale: scaleY,
    text: max
  }))), React.createElement("div", {
    className: styles.legendMore
  }, React.createElement(T, {
    desc: COPY$1.MORE
  }))));
};

const BASE_OPTIONS$1 = {
  title: {
    text: ''
  },
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: 'transparent'
  },
  plotOptions: {
    series: {
      dataLabels: {
        enabled: false
      }
    }
  }
}; // Sonar compliant

const sortWords = words => [...words].sort((a, b) => a.occurrences - b.occurrences);

const WordCloud = ({
  words = [],
  fixedColor = '#3FC688',
  colorPalette,
  showLegend,
  fontFamily = 'National 2'
}) => {
  if (!words.length) {
    return null;
  }

  const total = words.reduce((acc, {
    occurrences = 0
  }) => acc + occurrences, 0);
  let data = transformToHighChartsWordCloudModel(sortWords(words), total, fixedColor);
  const minOccurrence = getWeightBy(Math.min)(data);
  const maxOccurrence = getWeightBy(Math.max)(data);

  if (colorPalette) {
    data = colorizeModel(data, colorPalette);
  }

  const options = { ...BASE_OPTIONS$1,
    tooltip: tooltipConfig,
    series: [{
      type: 'wordcloud',
      style: {
        fontFamily: `"${fontFamily}"`,
        fontWeight: 400
      },
      rotation: {
        from: 0,
        to: 0
      },
      minFontSize: 12,
      data
    }]
  };
  return React.createElement(React.Fragment, null, React.createElement(HighchartsReact, {
    highcharts: wordCloudModule(Highcharts),
    options: options
  }), showLegend && React.createElement(Box, {
    display: "flex",
    flexJustify: "center"
  }, React.createElement(WordCloudLegend, {
    colorPalette: colorPalette,
    fixedColor: fixedColor,
    min: minOccurrence,
    max: maxOccurrence
  })));
};

const useStyles$2 = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    var _a, _b;

    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = // @ts-expect-error Fallback styles are acceptable
    ((_a = theme.typeColor) === null || _a === void 0 ? void 0 : _a[color]) || ((_b = theme.semanticColor) === null || _b === void 0 ? void 0 : _b[color]) || 'currentColor';
    return { ...fontSize,
      width: '1em',
      height: '1em',
      display: 'inline-block',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    };
  }
})); // https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging

const getDisplayName = Component => Component.displayName || Component.name || 'Component';

const withIcon = Component => {
  const WithIcon = ({
    color,
    title,
    size,
    ...rest
  }) => {
    const classes = useStyles$2({
      color,
      size
    });
    return React.createElement("svg", {
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: title ? 'img' : 'presentation',
      ...safeRest(rest)
    }, React.createElement(Component, null));
  };

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  return WithIcon;
};

const sentimentColors = {
  unknown: '#9DA5AA',
  neutral: '#F9BE00',
  negative: '#DB4D5C',
  positive: '#00BF6F'
};

const Negative = () => React.createElement("path", {
  fill: sentimentColors.negative,
  d: "M16.875 10.125a1.874 1.874 0 11-.001-3.749 1.874 1.874 0 01.001 3.749zM6 18.563a6 6 0 0112 0H6zM5.25 8.25a1.874 1.874 0 113.749-.001 1.874 1.874 0 01-3.749.001zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z",
  fillRule: "evenodd"
});

const NegativeIcon = withIcon(Negative);

const Neutral = () => React.createElement("path", {
  d: "M4.875 17.813h14.25v-3H4.875v3zm2.25-11.438a1.874 1.874 0 11.001 3.749 1.874 1.874 0 01-.001-3.749zm9.75 0a1.874 1.874 0 11.001 3.749 1.874 1.874 0 01-.001-3.749zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z",
  fill: sentimentColors.neutral,
  fillRule: "evenodd"
});

const NeutralIcon = withIcon(Neutral);

const Positive = () => React.createElement("path", {
  fill: sentimentColors.positive,
  d: "M16.875 10.125a1.874 1.874 0 11-.001-3.749 1.874 1.874 0 01.001 3.749M12 19.313a6 6 0 01-6-6h12a6 6 0 01-6 6M5.25 8.25a1.874 1.874 0 113.749-.001 1.874 1.874 0 01-3.749.001M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0",
  fillRule: "evenodd"
});

const PositiveIcon = withIcon(Positive);

const Unknown = () => React.createElement("path", {
  d: "M14.177 11.626c-1.011.874-1.408 1.425-1.427 2.374h-2.42c0-1.5.575-2.202 1.468-3 .773-.702 1.625-1.158 1.625-2.26 0-.892-.634-1.65-1.844-1.65-1.229 0-2.041.872-2.22 2.201L7 8.645C7.338 6.746 8.963 5 11.739 5 14.414 5 16 6.557 16 8.55c0 1.444-.872 2.24-1.823 3.076M11.5 19c-.89 0-1.5-.669-1.5-1.51S10.61 16 11.5 16s1.5.649 1.5 1.49S12.39 19 11.5 19M12 0C5.373 0 0 5.373 0 12c0 6.628 5.373 12 12 12 6.628 0 12-5.372 12-12 0-6.627-5.372-12-12-12",
  fill: sentimentColors.unknown,
  fillRule: "evenodd"
});

const UnknownIcon = withIcon(Unknown);
var Icons = {
  negative: NegativeIcon,
  neutral: NeutralIcon,
  positive: PositiveIcon,
  unknown: UnknownIcon
};

const SentimentIcon = ({
  sentiment,
  size,
  title,
  ...props
}) => {
  const Icon = Icons[sentiment];

  if (!Icon) {
    throw new Error('SentimentIcon: sentiment must be provided.');
  }

  return React.createElement(Icon, {
    size: size,
    title: title,
    // Sentiment icons are 24px square, Wrench defaults to 16px
    viewBox: "0 0 24 24",
    // Color will be overwritten by each Icon component
    color: "primary",
    ...props
  });
};

const sum = values => values.reduce((total, current) => total + current, 0);

const calculatePercentage = (count, total) => count && total ? count / total * 100 : 0;

const SENTIMENTS_ORDERED = ['positive', 'neutral', 'negative', 'unknown'];

const useSentimentChartItems = ({
  negative,
  neutral,
  positive,
  unknown
}) => React.useMemo(() => {
  const total = sum([negative, neutral, positive, unknown]);
  const sentiments = {
    negative,
    neutral,
    positive,
    unknown
  }; // Sort sentiments for display, calculate percentage

  const chartItems = SENTIMENTS_ORDERED.map(sentiment => ({
    sentiment,
    count: sentiments[sentiment],
    percentage: calculatePercentage(sentiments[sentiment], total)
  })); // Remove any sentiments with no responses

  return chartItems.filter(({
    count
  }) => count > 0);
}, [negative, neutral, positive, unknown]);

const COPY = defineMessages({
  PERCENT: {
    id: 'sentiment.PERCENT',
    defaultMessage: `{percentage, number, percent}`,
    description: '[Type: label][Vis: med] - percentage rendering'
  },
  SENTIMENT: {
    id: 'sentiment.SENTIMENT',
    defaultMessage: `{sentiment, select, 
      positive {Positive}
      negative {Negative}
      neutral {Neutral}
      other {Unknown}
    }`,
    description: '[Type: label][Vis: med] - sentiment labels'
  },
  SENTIMENT_WITH_PERCENT: {
    id: 'sentiment.SENTIMENT_WITH_PERCENT',
    defaultMessage: `{sentiment, select, 
      positive {Positive: {percentage, number, percent}}
      negative {Negative: {percentage, number, percent}}
      neutral {Neutral: {percentage, number, percent}}
      other {Unknown: {percentage, number, percent}}
    }`,
    description: '[Type: label][Vis: med] - sentiment labels with percentage'
  },
  // note this copy uses UNDETECTED instead of UNKNOWN because it is specific for analyze
  FILTER_BY_SENTIMENT: {
    id: 'sentiment.FILTER_BY_SENTIMENT',
    defaultMessage: `{sentiment, select, 
      positive {Filter by positive sentiment}
      negative {Filter by negative sentiment}
      neutral {Filter by neutral sentiment}
      other {Filter by undetected sentiment}
    }`,
    description: '[Type: title][Vis: med] - filter by sentiment'
  }
});
const useStyles$1 = createUseStyles(theme => ({
  chartItem: ({
    width
  }) => ({
    width: width && `${width}%`
  }),
  chartBar: ({
    color
  }) => ({
    border: `1px solid ${theme.bgColor.light}`,
    backgroundColor: color,
    height: '30px'
  })
}));
/* Chart segment layout */

const ChartSegment = ({
  color,
  icon,
  label,
  title,
  width
}) => {
  const {
    chartBar,
    chartItem
  } = useStyles$1({
    color,
    width
  });
  return React.createElement("div", {
    "aria-label": label,
    className: chartItem
  }, React.createElement(Box, {
    display: "flex",
    flexJustify: "center",
    mb: 3,
    mt: 3
  }, icon), React.createElement(Box, {
    mb: 3
  }, React.createElement("div", {
    className: chartBar
  })), React.createElement(Typography, {
    mb: 5,
    align: "center",
    variant: "bodySm"
  }, title));
};
/* Sentiment-analysis interface */


const SentimentBarChartSegment = ({
  percentage: percentage100Base,
  sentiment,
  size = 'md'
}) => {
  /** Copy's ICU formatting is 1 based (`1% === 0.01`) */
  const percentage = percentage100Base / 100;
  return React.createElement(ChartSegment, {
    color: sentimentColors[sentiment],
    icon: React.createElement(SentimentIcon, {
      sentiment: sentiment,
      size: size,
      title: t(COPY.SENTIMENT, {
        sentiment
      })
    }),
    label: t(COPY.SENTIMENT_WITH_PERCENT, {
      sentiment,
      percentage
    }),
    title: t(COPY.PERCENT, {
      percentage
    }),
    width: percentage100Base
  });
};

const SentimentBarChart = ({
  negative = 0,
  neutral = 0,
  positive = 0,
  unknown = 0,
  size
}) => {
  const chartItems = useSentimentChartItems({
    negative,
    neutral,
    positive,
    unknown
  });
  return React.createElement(Box, {
    display: "flex"
  }, chartItems.map(({
    sentiment,
    percentage
  }) => React.createElement(SentimentBarChartSegment, {
    key: sentiment,
    sentiment: sentiment,
    size: size,
    percentage: percentage
  })));
};

const BASE_OPTIONS = {
  title: {
    text: ''
  },
  credits: {
    enabled: false
  },
  tooltip: {
    enabled: false
  },
  chart: {
    type: 'pie',
    backgroundColor: null
  },
  plotOptions: {
    series: {
      dataLabels: {
        enabled: true,
        allowOverlap: true
      }
    },
    pie: {
      animation: {
        duration: 100,
        easing: 'easeOutQuad'
      },
      shadow: false,
      center: ['50%', '50%'],
      cursor: 'pointer',
      credits: {
        enabled: false
      }
    }
  }
};

const SentimentDonutLabel = ({
  point
}) => {
  return React.createElement(ThemeProvider, {
    theme: Theme
  }, React.createElement(SentimentIcon, {
    sentiment: point.name,
    size: "xl",
    title: ""
  }), React.createElement(Typography, {
    mb: 5,
    align: "center",
    variant: "bodySm"
  }, React.createElement("span", {
    "data-testid": `sentiment-${point.name}`
  }, Math.round(point.y), "%")));
};

var _a$1, _b$1, _c, _d; // support additional SVG attributes


(_a$1 = Highcharts.AST) === null || _a$1 === void 0 ? void 0 : _a$1.allowedAttributes.push('viewBox');
(_b$1 = Highcharts.AST) === null || _b$1 === void 0 ? void 0 : _b$1.allowedAttributes.push('preserveAspectRatio');
(_c = Highcharts.AST) === null || _c === void 0 ? void 0 : _c.allowedAttributes.push('fill-rule'); // Highcharts 9 removes some HTML tags and attributes: https://www.highcharts.com/blog/changelog/#highcharts-v9.0.0

(_d = Highcharts.AST) === null || _d === void 0 ? void 0 : _d.allowedAttributes.push('data-testid');

const SentimentDonutChart = ({
  negative = 0,
  neutral = 0,
  positive = 0,
  unknown = 0
}) => {
  const chartItems = useSentimentChartItems({
    negative,
    neutral,
    positive,
    unknown
  }).map(next => ({
    y: next.percentage,
    color: sentimentColors[next.sentiment],
    name: next.sentiment
  }));
  const options = { ...BASE_OPTIONS,
    series: [{
      innerSize: '70%',
      data: chartItems,
      dataLabels: {
        // allow svg rendering
        useHTML: true,

        formatter() {
          // Typescript bindings of highcharts binding of "this.point" missing

          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          const {
            point
          } = this; // see - https://github.com/highcharts/highcharts-react/issues/23
          // This will cause warnings to occur upon integration as renderToStaticMarkup will not process layout effects
          // These warning will go away in production they stem from useLayoutEffect not working inside SSR

          return renderToStaticMarkup(React.createElement(SentimentDonutLabel, {
            point: point
          }));
        },

        // label text colour
        color: 'black',
        // position label for scaling
        distance: 30,
        // hide connector line
        connectorWidth: 0
      }
    }]
  };
  return React.createElement(HighchartsReact, {
    highcharts: Highcharts,
    options: options
  });
};

const defaultChartColors = ['#00BF6F', '#507CB6', '#F9BE00', '#6BC8CD', '#FF8B4F', '#7D5E90', '#D25F90', '#C7B879', '#DB4D5C', '#768086'];

const splitLabel = str => {
  if (typeof str === 'string' && str.length < 13) {
    return 13;
  }

  for (let i = 13; i >= 0; i -= 1) {
    if (typeof str === 'string' && str.charAt(i) === ' ') {
      return i;
    }
  }

  return 11;
};

const formatLabelVerticalAxis = ({
  value
}) => {
  const newValue = value.toString();
  const splitPoint = splitLabel(newValue);
  const strTop = newValue.slice(0, splitPoint);
  const strBottom = newValue.slice(splitPoint, newValue.length);
  return `<div aria-label='${value}' title='${value}' align="center" style="width:60px;">
<div style="text-align: right">
${strTop}
</div>
<div style="overflow:hidden; text-overflow:ellipsis; max-height:14px; text-align:end; width:60px">
${strBottom}
</div>
</div>`;
};

function formatLabelHorizontalAxis(label) {
  const labelClipped = label.substring(0, 80);
  return label.length > 76 ? `${labelClipped}...` : label;
}

const themedOptions = theme => ({
  colors: defaultChartColors,
  chart: {
    spacingRight: 0,
    style: {
      fontFamily: theme.fontFamily.base
    },
    plotBackgroundColor: theme.bgColor.light
  },
  title: {
    style: {
      fontFamily: theme.fontFamily.base,
      fontWeight: theme.fontWeight.medium.toString(),
      fontSize: theme.fontSize.cardTitle.toString(),
      color: theme.typeColor.dark
    },
    text: '' // remove the chart title by default

  },
  xAxis: {
    title: {
      text: null // use "null" to hide x-axis title

    },
    labels: {
      style: {
        fontFamily: theme.fontFamily.base,
        fontWeight: theme.fontWeight.regular.toString(),
        color: theme.typeColor.dark,
        maxWidth: 10
      },
      overflow: 'justify',
      useHTML: true,

      formatter() {
        const label = this.axis.defaultLabelFormatter.call(this); // TODO: revisit this after dropping IE11 support

        return formatLabelHorizontalAxis(label);
      }

    },
    lineColor: theme.borderColor.border,
    tickLength: 0
  },
  yAxis: {
    // Do not change to an array.  Keep as single object.  See BarChart.tsx type assertion for YAxisOptions.
    labels: {
      overflow: 'justify',
      style: {
        fontFamily: theme.fontFamily.base,
        paddingTop: theme.spacing[3],
        fontWeight: theme.fontWeight.regular.toString(),
        color: theme.typeColor.dark
      },
      useHTML: true
    },
    title: {
      text: null
    },
    offset: 0,
    lineColor: theme.borderColor.border,
    lineWidth: 1,
    gridLineColor: theme.borderColor.muted
  },
  plotOptions: {
    series: {
      animation: false,
      enableMouseTracking: false,
      dataLabels: {
        style: {
          fontWeight: theme.fontWeight.medium.toString(),
          fontSize: theme.fontSize.bodySm.toString(),
          color: theme.typeColor.dark,
          textShadow: `-1px -1px 0 ${theme.typeColor.light}, 1px -1px 0 ${theme.typeColor.light}, -1px 1px 0 ${theme.typeColor.light}, 1px 1px 0 ${theme.typeColor.light}`
        },
        useHTML: true
      },
      point: {
        events: {
          legendItemClick() {
            return false;
          }

        }
      },
      events: {
        legendItemClick() {
          return false; // disables legend item toggling
        }

      },
      states: {
        hover: {
          enabled: false
        },
        inactive: {
          enabled: false
        }
      }
    }
  },
  legend: {
    labelFormatter() {
      return `<div style="
      overflow: hidden;
      text-overflow: ellipsis;
      ">${this.name}</div>`;
    },

    enabled: false,
    floating: false,
    borderWidth: 0,
    symbolRadius: 2,
    x: 0,
    y: 5,
    useHTML: true,
    itemDistance: 20,
    itemStyle: {
      color: theme.typeColor.dark,
      fontFamily: theme.fontFamily.base,
      fontSize: theme.fontSize.bodySm.toString(),
      fontWeight: theme.fontWeight.light.toString(),
      lineHeight: '20px'
    },
    itemHoverStyle: {
      color: theme.typeColor.dark
    }
  },
  tooltip: {
    animation: false,
    hideDelay: 100,
    backgroundColor: theme.bgColor.dark,
    borderWidth: 0,
    shadow: false,
    headerFormat: '',
    followPointer: true,
    style: {
      fontFamily: theme.fontFamily.base,
      fontSize: theme.fontSize.bodySm.toString(),
      color: theme.typeColor.dark
    },
    useHTML: true
  },
  credits: {
    enabled: false
  },
  accessibility: {
    enabled: true
  }
});

const useDefaultOptions = () => {
  const theme = useTheme();
  return themedOptions(theme);
};

var _a, _b;

HighchartsAccessibility(Highcharts); // support additional HTML/SVG attributes

(_a = Highcharts.AST) === null || _a === void 0 ? void 0 : _a.allowedAttributes.push('align');
(_b = Highcharts.AST) === null || _b === void 0 ? void 0 : _b.allowedAttributes.push('title');
const useStyles = createUseStyles({
  container: {
    '& .highcharts-container': {
      direction: 'inherit'
    }
  }
});
/** Wrapper around `HighchartsReact` with default values - base of all other charts */

const BaseChart = ({
  options
}) => {
  const base = useDefaultOptions();
  const {
    container
  } = useStyles();
  return React.createElement(HighchartsReact, {
    containerProps: {
      style: {
        width: '100%',
        height: '100%'
      },
      className: container
    },
    highcharts: Highcharts,
    options: merge(base, options)
  });
};

const getAreaChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  const series = data.map(el => ({ ...el,
    type: 'area'
  }));
  return merge( // overwritable defaults
  {
    plotOptions: {
      area: {
        dataLabels: {
          enabled: true,
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        stacking: isPercentage ? 'percent' : 'normal'
      }
    },
    tooltip: {
      // TODO: add show tooltip
      enabled: false
    },
    yAxis: {
      labels: {
        format: isPercentage ? '{value}%' : '{value}'
      },
      reversedStacks: false
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'area'
    }
  });
};

const AreaChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const areaChartOptions = getAreaChartOptions(data, options, isPercentage, decimalPlaces);
  return React.createElement(BaseChart, {
    options: areaChartOptions
  });
};
/** checks for `{name: string, data: any}[]` */


const isTwoDimensionalChartData = a => Array.isArray(a) && a.every(i => typeof i === 'object' && 'name' in i && 'data' in i && i.data.every(d => typeof d === 'number'));

const getBarChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  return merge( // overwritable defaults
  {
    plotOptions: {
      bar: {
        colorByPoint: true,
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          inside: false
        }
      }
    },
    xAxis: {
      labels: {
        formatter: formatLabelVerticalAxis
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series: isTwoDimensionalChartData(data) ? data.map(d => ({
      name: d.name,
      data: d.data,
      type: 'bar'
    })) : [{
      data,
      type: 'bar'
    }]
  });
};

const BarChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const barChartOptions = getBarChartOptions(data, options, isPercentage, decimalPlaces);
  return React.createElement(BaseChart, {
    options: barChartOptions
  });
};

const getColumnChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  return merge( // overwritable defaults
  {
    plotOptions: {
      column: {
        colorByPoint: true,
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          inside: false
        }
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series: isTwoDimensionalChartData(data) ? data.map(d => ({
      name: d.name,
      data: d.data,
      type: 'column'
    })) : [{
      data,
      type: 'column'
    }]
  });
};

const ColumnChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const columnChartOptions = getColumnChartOptions(data, options, isPercentage, decimalPlaces);
  return React.createElement(BaseChart, {
    options: columnChartOptions
  });
};

function getPieChartOptions(data, options) {
  const pieChartData = {
    data: data.map(({
      name,
      value,
      numberOfResponses
    }) => ({
      name,
      y: value,
      numberOfResponses
    })),
    type: 'pie'
  };
  return merge(options, // not overwritable
  {
    series: [pieChartData],
    chart: {
      type: 'pie'
    }
  });
}

const PieChart = ({
  data,
  options
}) => {
  const pieChartOptions = getPieChartOptions(data, options);
  return React.createElement(BaseChart, {
    options: pieChartOptions
  });
};
/** Build Donut specific options - rest is done in PieChart */


const getDonutChartOptions = options => merge( // overwritable defaults
{
  plotOptions: {
    pie: {
      innerSize: '40%'
    }
  }
}, options);
/** Donut chart is a special type of PieChart (and uses it internally) */


const DonutChart = ({
  data,
  options
}) => {
  const donutChartOptions = getDonutChartOptions(options);
  return React.createElement(PieChart, {
    data: data,
    options: donutChartOptions
  });
};

const getLineChartOptions = (data, options, isPercentage = false, decimalPlaces = 0) => {
  const series = data.map(el => ({ ...el,
    type: 'line'
  }));
  return merge( // overwritable defaults
  {
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        }
      }
    },
    tooltip: {
      // TODO: add show tooltip
      enabled: false
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'line'
    }
  });
};

const LineChart = ({
  data,
  options,
  isPercentage,
  decimalPlaces
}) => {
  const lineChartOptions = getLineChartOptions(data, options, isPercentage, decimalPlaces);
  return React.createElement(BaseChart, {
    options: lineChartOptions
  });
};

const getStackedBarChartOptions = (data, options, isPercentage = false, showLabels = false, decimalPlaces = 0) => {
  const series = data.map(stack => ({ ...stack,
    type: 'bar'
  }));
  return merge( // overwritable defaults
  {
    legend: {
      enabled: true,
      reversed: true // reverse series labels have have same order as `data` (Highcharts flips them) ref: https://www.highcharts.com/demo/bar-stacked

    },
    plotOptions: {
      bar: {
        stacking: isPercentage ? 'percentage' : 'normal',
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        groupPadding: 0,
        maxPointWidth: 100 // TODO: refine this

      }
    },
    xAxis: {
      labels: {
        enabled: showLabels
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'bar'
    }
  });
};

const StackedBarChart = ({
  data,
  options,
  isPercentage,
  showLabels,
  decimalPlaces
}) => {
  const barChartOptions = getStackedBarChartOptions(data.slice().reverse(), // reverse series data have have same order as data inserted (Highcharts flips them)
  options, isPercentage, showLabels, decimalPlaces);
  return React.createElement(BaseChart, {
    options: barChartOptions
  });
};

const getStackedColumnChartOptions = (data, options, isPercentage = false, showLabels = false, decimalPlaces = 0) => {
  const series = data.map(stack => ({ ...stack,
    type: 'column'
  }));
  return merge( // overwritable defaults
  {
    legend: {
      enabled: true,
      reversed: true // reverse series labels have have same order as `data` (Highcharts flips them) ref: https://www.highcharts.com/demo/bar-stacked

    },
    plotOptions: {
      column: {
        stacking: isPercentage ? 'percentage' : 'normal',
        dataLabels: {
          format: `{y:.${decimalPlaces}f}${isPercentage ? '%' : ''}`,
          style: {
            textOutline: '1px contrast'
          },
          useHTML: false // needed for textOutline

        },
        groupPadding: 0,
        maxPointWidth: 100 // TODO: refine this

      }
    },
    xAxis: {
      labels: {
        enabled: showLabels
      }
    },
    yAxis: {
      labels: {
        format: `{value}${isPercentage ? '%' : ''}`
      }
    }
  }, options, // not overwritable
  {
    series,
    chart: {
      type: 'column'
    }
  });
};

const StackedColumnChart = ({
  data,
  options,
  isPercentage,
  showLabels,
  decimalPlaces
}) => {
  const columnChartOptions = getStackedColumnChartOptions(data.slice().reverse(), // reverse series data have have same order as data inserted (Highcharts flips them)
  options, isPercentage, showLabels, decimalPlaces);
  return React.createElement(BaseChart, {
    options: columnChartOptions
  });
};

export { AreaChart, BarChart, BaseChart, ColumnChart, DonutChart, LineChart, PieChart, RatingChart, RatingChartLegend, SentimentBarChart, SentimentDonutChart, SentimentIcon, StackedBarChart, StackedColumnChart, WordCloud };